/**
 * Convert a display name into a URL-safe slug candidate. Mirrors the API-side
 * `OrbitalX.Business.Helpers.SlugHelper.Slugify` so the dashboard preview
 * matches what the server will accept.
 *
 * - Lowercases
 * - Strips accents (NFD + drop combining marks)
 * - Replaces any non-alphanumeric run with a single hyphen
 * - Trims leading/trailing hyphens
 * - Returns an empty string when nothing remains
 */
export function slugify(input: string | null | undefined): string {
  if (!input) return ''
  const normalized = input.normalize('NFD').replace(/\p{M}/gu, '')
  const collapsed = normalized
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return collapsed
}

/**
 * Shortens a suggested slug to `maxLength`, cutting at the last whole word so the suggestion stays
 * valid. A web title may be 200 characters while its slug may only be 180 (ORB-944), so without this
 * a long title's suggestion would fail the format check and send the PM back to shorten it by hand.
 */
export function fitSlug(slug: string, maxLength: number): string {
  if (slug.length <= maxLength) return slug
  const cut = slug.slice(0, maxLength)
  const lastHyphen = cut.lastIndexOf('-')
  return (lastHyphen > 0 ? cut.slice(0, lastHyphen) : cut).replace(/-+$/, '')
}

/** Company and workspace slugs. The company slug is a subdomain label, which DNS caps at 63. */
export const SLUG_MAX_LENGTH = 50

/**
 * A web's slug is only a URL path segment (the landing page and BigMarker's sub_url), so it can
 * run close to the length of an event title (ORB-944). 180 is the longest sub_url BigMarker's API
 * accepts. Mirrors `SlugValidationConstants.WebMaxLength`.
 */
export const WEB_SLUG_MAX_LENGTH = 180

/**
 * Returns true when the string matches the canonical slug format the API
 * enforces: lowercase alphanumeric segments joined by single hyphens, 3 to
 * `maxLength` chars (50 unless the caller is a web).
 */
export function isValidSlugFormat(slug: string | null | undefined, maxLength: number = SLUG_MAX_LENGTH): boolean {
  if (!slug) return false
  if (slug.length < 3 || slug.length > maxLength) return false
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
}
